import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Statement } from '../components/main/review/human-validation/models/statement';
import { SubmissionRule, SubmissionRuleActions } from './template-data.service';
import { SpreadingState } from '../components/main/spreading/state/manager';
import { StatementDataService } from '../components/main/spreading/data/statement-data.service';
import { StatementDataCache } from '../components/main/spreading/data/cache';
import { SubmissionRulesService, SubmissionRuleEvaluationResult } from './submission-rules.service';
import { StatementService } from './statement.service';
import { DYNAMIC_ADJUSTMENT_STATEMENT } from '@utils/constants';
import { Row } from '@components/main/spreading/models/models';
import { ScaleType } from './rounding.service';

export interface ValidationResult {
  statementId: number;
  statementType: string;
  statementDate: string;
  periodKeyUuid?: string; // Add period key UUID for precise period identification
  status: 'passing' | 'soft-stop' | 'hard-stop';
  rules: Array<{
    message: string;
    action: SubmissionRuleActions;
    rowLabel: string;
    cellValue: string;
    rowRef?: string; // Add row reference ID for precise navigation
  }>;
}

interface SubmissionRulesMap {
  [templateItemRef: string]: SubmissionRule[];
}



interface PreLoadedSnapshots {
  [cacheKey: string]: {
    stdFrameRefSnapshot?: Record<string, unknown>;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ValidateSubmissionRuleService {

  submissionRuleTabStatusSummary: any = {};
  
  constructor(
    private statementDataService: StatementDataService,
    private submissionRulesService: SubmissionRulesService,
    private statementService: StatementService
  ) {}

  syncCurrentStatementTabStatus(state: SpreadingState): void {
    if (!state?.templateHasSubmissionRules || !state.statement) {
      return;
    }
    this.submissionRuleTabStatusSummary[state.statement.id] =
      state.submissionRuleResultsStatementLevelStatus;
  }

  private createValidationResult(statementId: number, statementType = '', statementDate = '', periodKeyUuid = ''): ValidationResult {
    return {
      statementId,
      statementType,
      statementDate,
      periodKeyUuid,
      status: 'passing',
      rules: []
    };
  }

  private updateValidationStatus(result: ValidationResult, action: SubmissionRuleActions): void {
    if (action === SubmissionRuleActions.HARD_STOP) {
      result.status = 'hard-stop';
    } else if (action === SubmissionRuleActions.SOFT_STOP && result.status !== 'hard-stop') {
      result.status = 'soft-stop';
    }
  }

  private processRowsForValidation(rows: Row[], validationResults: ValidationResult, colIdx?: number): void {
    for (const row of rows) {
      if (row.cells) {
        const cellsToProcess = colIdx !== undefined ? [row.cells[colIdx]] : row.cells;
        
        for (const cell of cellsToProcess) {
          if (cell?.submissionRuleResults?.length) {
                      for (const ruleResult of cell.submissionRuleResults) {
            validationResults.rules.push({
              message: ruleResult.message,
              action: ruleResult.action,
              rowLabel: row.label,
              cellValue: cell.rawText || cell.value?.toString() || '',
              rowRef: row.ref
            });
            this.updateValidationStatus(validationResults, ruleResult.action);
          }
          }
        }
      }
      if (row.children?.length) {
        this.processRowsForValidation(row.children, validationResults, colIdx);
      }
    }
  }

  async validateAllStatementsWithFullState(
    statements: Statement[], 
    submissionRules: SubmissionRulesMap, 
    cache: StatementDataCache = null, 
    currentState: SpreadingState,
    preLoadedSnapshots: PreLoadedSnapshots,
    reloadDasSnapshots: boolean = false
  ): Promise<ValidationResult[]> {
    const statementsToLoad = statements.filter(s => s.id !== currentState.statement.id);
    let loadedStatements: Statement[] = [];
    
    if (reloadDasSnapshots && statementsToLoad.length > 0) {
      const snapshotRequest = this.statementService.loadDASStatementSnapshot(currentState.statement.uuid, currentState.documentFile.id)
      const [snapshotResult, statementResult] = await Promise.all([firstValueFrom(snapshotRequest), this.statementDataService.loadStatementsByIds(statementsToLoad.map(s => s.id))]);
      currentState.dasRefSnapshot = snapshotResult;
      loadedStatements = statementResult;
    } else if (statementsToLoad.length > 0) {
      loadedStatements = await this.statementDataService.loadStatementsByIds(statementsToLoad.map(s => s.id));
    }
    
    let dasSnapshots: Record<string, unknown[]> = this.buildDasRefSnapshots(currentState);
    
    loadedStatements.push(currentState.statement);

    const stdFrameRefSnapshots = await this.loadStdFrameRefSnapshots(loadedStatements, preLoadedSnapshots);

    const statementLoadingPromises = loadedStatements.map(async (statement) => {
      try {
        // For current statement, use existing submission rule results instead of re-running validation.
        // Still record its tab status from the live state so its tab is correct once it becomes non-active.
        if (statement.id === currentState.statement.id) {
          this.submissionRuleTabStatusSummary[statement.id] = currentState.submissionRuleResultsStatementLevelStatus;
          return this.processCurrentStatementWithExistingResults(statement, currentState);
        }
        
        // For other statements, load and prepare state as in spreading component
        const state = await this.loadAndPrepareState(currentState, statement, submissionRules, cache, stdFrameRefSnapshots, dasSnapshots);
        const columnValidationResults: ValidationResult[] = [];

        this.submissionRuleTabStatusSummary[statement.id] = state.submissionRuleResultsStatementLevelStatus;
        
        for (let colIdx = 0; colIdx < state.columns.length; colIdx++) {
          const column = state.columns[colIdx];
          const columnValidationResult = this.createValidationResult(
            statement.id,
            statement.statementType, 
            column.statementDate,
            column.periodKeyUuid
          );
          
          this.processRowsForValidation(this.getRows(state), columnValidationResult, colIdx);
          columnValidationResults.push(columnValidationResult);
        }
        
        return columnValidationResults;
      } catch (error) {
        console.error(`Failed to validate statement ${statement.id}:`, error);
        return [];
      }
    });
    
    const allValidationResults = await Promise.all(statementLoadingPromises);
    return allValidationResults.flat();
  }

  private buildDasRefSnapshots(currentState: SpreadingState): Record<string, unknown[]> {
    let dasRefSnapshots = currentState.dasRefSnapshot || {}
    if (currentState.statement.statementType === DYNAMIC_ADJUSTMENT_STATEMENT) {
      const snapshot: Record<string, unknown[]> = {};
      const rows = this.getRows(currentState);
      
      for (const row of rows) {
        if (row.ref && row.cells) {
          snapshot[row.ref] = row.cells.map(cell => cell.value);
        }
      }
      
      dasRefSnapshots = { ...dasRefSnapshots, ...snapshot };
    }
    return dasRefSnapshots;
  }

  /**
   * Process current statement using existing submission rule results from the spreading state
   * This avoids re-running validation for the current statement since it already has real-time results
   */
  private processCurrentStatementWithExistingResults(currentStatement: Statement, currentSpreadingState?: SpreadingState): ValidationResult[] {
    const columnValidationResults: ValidationResult[] = [];
    
    // Use the provided current spreading state that has the submission rule results
    if (!currentSpreadingState) {
      console.warn('Current spreading state not provided for submission rule validation');
      return [];
    }
    
    const rows = this.getRows(currentSpreadingState);
    
    // Process each column
    for (let colIdx = 0; colIdx < currentSpreadingState.columns.length; colIdx++) {
      const column = currentSpreadingState.columns[colIdx];
      const columnValidationResult = this.createValidationResult(
        currentStatement.id,
        currentStatement.statementType, 
        column.statementDate,
        column.periodKeyUuid
      );
      
      // Use existing submission rule results from the current state
      this.processRowsForValidation(rows, columnValidationResult, colIdx);
      columnValidationResults.push(columnValidationResult);
    }
    
    return columnValidationResults;
  }

  private async loadStdFrameRefSnapshots(
    statements: Statement[], 
    preLoadedSnapshots?: PreLoadedSnapshots
  ): Promise<Record<string, unknown>> {
    // Collect all unique periodKeyUuids from all DAS statements
    const dasStatements = statements.filter(s => s.statementType === DYNAMIC_ADJUSTMENT_STATEMENT);
    const allPeriodKeyUuids = this.getAllPeriodKeyUuidsFromStatements(dasStatements);
    
    if (dasStatements.length === 0 || allPeriodKeyUuids.size === 0) {
      return <Record<string, unknown>>{};
    }
    
    const cacheKey = this.generateCacheKey(dasStatements);
    
    if (preLoadedSnapshots?.[cacheKey]?.stdFrameRefSnapshot) {
      return preLoadedSnapshots[cacheKey].stdFrameRefSnapshot;
    }
    
    const stdFrameRefSnapshots = await this.loadStdFrameSnapshot(
      dasStatements[0], 
      Array.from(allPeriodKeyUuids)
    );
    
    return stdFrameRefSnapshots;
  }

  private async loadStdFrameSnapshot(
    statement: Statement, 
    periodKeyUuids: string[]
  ): Promise<Record<string, unknown>> {
    try {
      const stdFrameSnapshot = await firstValueFrom(this.statementService.loadSTDFrameRefSnapshot(
        statement.companyId,
        periodKeyUuids,
        statement.documentFileId,
        statement.spreadingTemplateId
      ));
      return stdFrameSnapshot;
    } catch (error) {
      console.warn(`Failed to load standard frame snapshot for statement ${statement.id}:`, error);
    }
    return <Record<string, unknown>>{};
  }

  generateCacheKey(statements: Statement[]): string {
    const allPeriodKeyUuids = this.getAllPeriodKeyUuidsFromStatements(statements);
    // Data categorization, all statements have same companyId, documentFileId, templateId
    const stmt = statements[0];
    const companyId = stmt.companyId;
    const documentFileId = stmt.documentFileId;
    const spreadingTemplateId = stmt.spreadingTemplateId;
    const periodKeyUuids = Array.from(allPeriodKeyUuids).sort().join(',');
    return `${companyId}-${documentFileId}-${spreadingTemplateId}-${periodKeyUuids}`;
  }

  private getAllPeriodKeyUuidsFromStatements(dasStatements: Statement[]): Set<string> {
    const allPeriodKeyUuids = new Set<string>(
      dasStatements.flatMap(statement => 
        statement?.columns?.map(col => col.periodKeyUuid) || []
      )
    );
    return allPeriodKeyUuids;
  }

  private async loadAndPrepareState(
    currentState: SpreadingState,
    statement: Statement, 
    submissionRules: SubmissionRulesMap, 
    cache: StatementDataCache, 
    stdFrameRefSnapshots: Record<string, unknown>,
    dasSnapshots: Record<string, unknown[]>
  ): Promise<SpreadingState> {
    const state = await this.statementDataService.loadStateForValidation(statement, cache);
    
    if (state.statementType === DYNAMIC_ADJUSTMENT_STATEMENT) {
      state.dasRefSnapshot = dasSnapshots;
      state.stdFrameRefSnapshot = stdFrameRefSnapshots;
    }
    state.reviewQueueItem = currentState.reviewQueueItem;
    state.submissionRules = submissionRules;
    state.templateHasSubmissionRules = true;
    state.calculate();
    this.calculateSubmissionRulesForRows(this.getRows(state), state);
    
    return state;
  }

  private getRows(state: SpreadingState): Row[] {
    return state.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? state.sourceRows : state.rows;
  }

  private calculateSubmissionRulesForRows(rows: Row[], state: SpreadingState): void {
    for (const row of rows) {
      if (row.ref && state.submissionRules?.[row.ref] && row.cells) {
        const submissionRulesForRow = state.submissionRules[row.ref];
        const roundingFactor = <ScaleType>(state.reviewQueueItem?.multiplyByFactor ?? 'ACTUALS');
        for (const cell of row.cells) {
          const submissionRuleResults: SubmissionRuleEvaluationResult[] = [];
          
          for (const submissionRule of submissionRulesForRow) {
            const ruleResult = this.submissionRulesService.evaluateSubmissionRule(submissionRule, cell.value, roundingFactor);
            if (ruleResult) {
              submissionRuleResults.push(ruleResult);
            }
          }
          
          cell.submissionRuleResults = submissionRuleResults;
          cell.submissionRuleResultsStatus = state.determineCellSubmissionRuleResultsStatus(submissionRuleResults);
        }
      }
      
      if (row.children?.length) {
        this.calculateSubmissionRulesForRows(row.children, state);
      }
    }
  }
}